import type { ComplianceIssue } from '@qlk/shared';
import {
  COMPLIANCE_ISSUE_TYPES,
  COMPLIANCE_ISSUE_TYPE_LABEL,
  COMPLIANCE_STATUSES,
  COMPLIANCE_STATUS_LABEL,
} from '@qlk/shared';
import { useMemo, useState } from 'react';

import { coQuyen, useDuongDanTrang, useToi } from '../../../lib/auth.js';
import {
  EmptyState,
  KhoiChuaCoKenh,
  KhoiMuc,
  Nut,
  OChon,
  PageHeader,
  SegmentedControl,
  StatStrip,
  ToolbarBang,
  type OChiSo,
} from '../../../ui/index.js';
import {
  useCapNhatSuCo,
  useDanhSachSuCo,
  useHanhDongSuCo,
  useKenhChonSuCo,
  useNhapSuCoCsv,
  useQuetLai,
  useTaoSuCo,
  useThongKeTuanThu,
  useTomTatTuanThu,
} from '../api.js';
import { BangSuCo } from '../components/BangSuCo.js';
import { FormNhapCsv } from '../components/FormNhapCsv.js';
import { FormSuCo } from '../components/FormSuCo.js';
import { KhungChecklist } from '../components/KhungChecklist.js';
import { KhungNhatKy } from '../components/KhungNhatKy.js';

/** Số 0 là tin tốt nên để màu xanh, số > 0 màu đỏ — theo ảnh thiết kế. */
function SoChiSo({ giaTri }: { giaTri: number | undefined }) {
  if (giaTri === undefined) return <>—</>;
  return <span className={giaTri === 0 ? 'text-ok-chu' : 'text-bad-chu'}>{giaTri}</span>;
}

export function TrangBanQuyen() {
  const duongDan = useDuongDanTrang('QUẢN TRỊ');
  const toi = useToi();
  const coSua = coQuyen(toi.data?.permissions, 'compliance', 'edit');

  const [chiDangMo, setChiDangMo] = useState(true);
  const [loai, setLoai] = useState('');
  const [trangThai, setTrangThai] = useState('');
  const [bangMo, setBangMo] = useState<'khong' | 'them' | 'csv'>('khong');

  const loc = {
    ...(chiDangMo ? { onlyOpen: true } : {}),
    ...(loai ? { type: loai } : {}),
    ...(trangThai ? { status: trangThai } : {}),
  };

  const danhSach = useDanhSachSuCo(loc);
  const tomTat = useTomTatTuanThu();
  const thongKe = useThongKeTuanThu();
  const kenh = useKenhChonSuCo();
  const quetLai = useQuetLai();
  const taoSuCo = useTaoSuCo();
  const capNhat = useCapNhatSuCo();
  const hanhDong = useHanhDongSuCo();
  const nhapCsv = useNhapSuCoCsv();

  const dong = useMemo(
    () => danhSach.data?.pages.flatMap((t) => t.items) ?? [],
    [danhSach.data?.pages],
  );

  const dsKenh = useMemo(
    () => (kenh.data ?? []).map((k) => ({ id: k.id, name: k.name })),
    [kenh.data],
  );

  const s = tomTat.data;
  const tk = thongKe.data;

  /*
   * Bốn `StatCard` rời thành **một** `StatStrip`. Giữ nguyên luật màu của số:
   * 0 là tin tốt nên xanh, khác 0 là đỏ (xem `SoChiSo`) — ở trang này con số
   * **không** theo chiều "tăng là tốt", nên không dùng `delta`.
   */
  const chiSo: OChiSo[] = [
    {
      nhan: 'Gậy bản quyền',
      giaTri: <SoChiSo giaTri={s?.copyrightStrikes} />,
      ...(s && tk
        ? {
            phu:
              s.copyrightStrikes === 0
                ? `Toàn bộ ${String(tk.channelCount)} kênh an toàn`
                : `${String(tk.channelsWithStrike)} kênh đang có gậy`,
          }
        : {}),
    },
    {
      nhan: 'Cảnh cáo cộng đồng',
      giaTri: <SoChiSo giaTri={s?.communityStrikes} />,
      ...(s === undefined
        ? {}
        : {
            phu:
              s.communityStrikes === 0
                ? 'Không có cảnh cáo hiệu lực'
                : `${String(s.communityStrikes)} cảnh cáo đang hiệu lực`,
          }),
    },
    {
      nhan: 'Khiếu nại Content ID mở',
      giaTri: <SoChiSo giaTri={s?.openContentIdClaims} />,
      ...(tk ? { phu: `${String(tk.internalMusicClaims)} claim nhầm từ nhạc nội bộ` } : {}),
    },
    {
      nhan: 'Hạn chế quảng cáo',
      giaTri: <SoChiSo giaTri={s?.adLimited} />,
      ...(tk
        ? {
            phu:
              tk.adLimitedUnderReview > 0 ? 'Đang yêu cầu xem xét lại' : 'Chưa gửi yêu cầu xem xét',
          }
        : {}),
    },
  ];

  function doiTrangThai(suCo: ComplianceIssue) {
    capNhat.mutate({ id: suCo.id, input: { status: 'resolved' } });
  }

  return (
    <>
      <PageHeader
        breadcrumb={duongDan}
        title="Bản quyền & tuân thủ"
        {...(tk ? { phu: `${String(tk.channelCount)} kênh được quét` } : {})}
        giaiThich="Gậy, khiếu nại Content ID, hạn chế quảng cáo và kiểm tra trước khi đăng."
        actions={
          coSua && (
            <>
              <Nut
                kieu="phu"
                onClick={() => {
                  setBangMo((cu) => (cu === 'csv' ? 'khong' : 'csv'));
                }}
              >
                Nhập CSV
              </Nut>
              <Nut
                kieu="phu"
                onClick={() => {
                  setBangMo((cu) => (cu === 'them' ? 'khong' : 'them'));
                }}
              >
                + Thêm sự cố
              </Nut>
              <Nut
                kieu="chinh"
                dangChay={quetLai.isPending}
                onClick={() => {
                  quetLai.mutate();
                }}
              >
                Quét lại tất cả kênh
              </Nut>
            </>
          )
        }
      />

      <KhoiChuaCoKenh tenTrang="Bản quyền & tuân thủ" />

      {quetLai.data && (
        <p
          role="status"
          className="mb-4 rounded-[10px] bg-ok-nen px-4 py-3 text-co-ban text-ok-chu"
        >
          Quét xong: tạo mới {quetLai.data.daTao} sự cố, bỏ qua {quetLai.data.boQua} sự cố đã có,
          cập nhật sức khoẻ {quetLai.data.daCapNhatSucKhoe} kênh.
        </p>
      )}

      {quetLai.error && (
        <p
          role="alert"
          className="mb-4 rounded-[10px] bg-bad-nen px-4 py-3 text-co-ban text-bad-chu"
        >
          {quetLai.error.message}
        </p>
      )}

      <div className="mb-5">
        <StatStrip aria-label="Chỉ số tuân thủ" o={chiSo} />
      </div>

      {bangMo === 'them' && coSua && (
        <div className="mb-5">
          <KhoiMuc tieuDe="Thêm sự cố">
            <FormSuCo
              kenh={dsKenh}
              dangGui={taoSuCo.isPending}
              loi={taoSuCo.error?.message ?? null}
              onHuy={() => {
                setBangMo('khong');
              }}
              onGui={(input) => {
                taoSuCo.mutate(input, {
                  onSuccess: () => {
                    setBangMo('khong');
                  },
                });
              }}
            />
          </KhoiMuc>
        </div>
      )}

      {bangMo === 'csv' && coSua && (
        <div className="mb-5">
          <KhoiMuc tieuDe="Nhập sự cố từ CSV">
            <FormNhapCsv
              dangGui={nhapCsv.isPending}
              loi={nhapCsv.error?.message ?? null}
              ketQua={nhapCsv.data ?? null}
              onHuy={() => {
                setBangMo('khong');
                nhapCsv.reset();
              }}
              onGui={(input) => {
                nhapCsv.mutate(input);
              }}
            />
          </KhoiMuc>
        </div>
      )}

      <section aria-labelledby="su-co-tieu-de" className="mb-5">
        <h2 id="su-co-tieu-de" className="sr-only">
          Danh sách sự cố
        </h2>

        {/*
          Nút "Chỉ xem đang mở / Xem cả đã xử lý" thành `SegmentedControl` hai
          mục: một nút bật-tắt bắt người dùng đọc nhãn để đoán **trạng thái
          hiện tại**, còn hai mục thì chỉ cần nhìn mục nào đang sáng.
        */}
        <ToolbarBang
          trai={
            <SegmentedControl
              aria-label="Phạm vi sự cố"
              giaTri={chiDangMo ? 'dang-mo' : 'tat-ca'}
              onChange={(v) => {
                setChiDangMo(v === 'dang-mo');
                setTrangThai('');
              }}
              muc={[
                { giaTri: 'dang-mo', nhan: 'Đang mở' },
                { giaTri: 'tat-ca', nhan: 'Tất cả' },
              ]}
            />
          }
          phai={
            <>
              <OChon
                label="Loại sự cố"
                value={loai}
                onChange={setLoai}
                options={[
                  { value: '', label: 'Mọi loại' },
                  ...COMPLIANCE_ISSUE_TYPES.map((t) => ({
                    value: t,
                    label: COMPLIANCE_ISSUE_TYPE_LABEL[t],
                  })),
                ]}
              />
              <OChon
                label="Trạng thái"
                value={trangThai}
                onChange={(v) => {
                  setTrangThai(v);
                  if (v) setChiDangMo(false);
                }}
                options={[
                  { value: '', label: 'Mọi trạng thái' },
                  ...COMPLIANCE_STATUSES.map((t) => ({
                    value: t,
                    label: COMPLIANCE_STATUS_LABEL[t],
                  })),
                ]}
              />
            </>
          }
        />

        {danhSach.error ? (
          <div className="rounded-the border border-vien bg-be-mat">
            <EmptyState
              loi
              title="Không tải được danh sách sự cố"
              description={danhSach.error.message}
            />
          </div>
        ) : (
          <BangSuCo
            dong={dong}
            dangTai={danhSach.isPending}
            conNua={danhSach.hasNextPage}
            dangTaiThem={danhSach.isFetchingNextPage}
            onTaiThem={() => void danhSach.fetchNextPage()}
            coSua={coSua}
            dangChay={hanhDong.isPending || capNhat.isPending}
            onHanhDong={(suCo, hd) => {
              hanhDong.mutate({ id: suCo.id, input: { action: hd } });
            }}
            onDaXuLy={doiTrangThai}
          />
        )}

        {hanhDong.error && (
          <p role="alert" className="mt-3 text-co-ban text-bad-chu">
            {hanhDong.error.message}
          </p>
        )}
      </section>

      <div className="grid grid-cols-1 gap-5 @min-[900px]/noi-dung:grid-cols-2">
        <KhungChecklist coSua={coSua} />
        <KhungNhatKy />
      </div>
    </>
  );
}
